"use client";

import { useMemo } from "react";
import Link from "next/link";
import type { VratCategory, VratHubDate, VratHubYear } from "@/lib/vrat-dates";
import { currentIndiaDate, formatVedicEngineDate } from "@/lib/vedic-engine-date";

/**
 * Vrat-calendar hub table (rule 23/24). Lists every Hindu vrat & fast type with
 * the NEXT engine-computed date + a link to its dedicated page. EVERY date here
 * is the value the backend astrology engine returned for that tithi/ingress — no
 * date is hand-typed. The "next" date per vrat is derived by comparing engine
 * dates to today's IST date. Locale-aware so the en + hi hub render the SAME
 * component (no per-locale date copy to drift).
 *
 * `years` is never empty: `loadVratHubYears` throws before render when the
 * engine cannot supply every family for a year, so the page never ships
 * without dates. The next date per vrat is the first on or after today across
 * all loaded years, so late December rolls into the next year instead of a
 * dash. The seven links render on every row whether or not a next date remains.
 *
 * When the engine moves the fast off the tithi date (`fasting_date`, e.g. a
 * Purnima fast kept on Chaturdashi), the row names the fast day under the date,
 * the same way EngineDateTable does on the family page the row links to.
 */
interface VratRowDef {
  key: VratCategory;
  label_en: string;
  label_hi: string;
  sub_en: string;
  sub_hi: string;
  href_en: string;
  href_hi: string;
}

const VRAT_ROWS: VratRowDef[] = [
  {
    key: "ekadashi",
    label_en: "Ekadashi",
    label_hi: "एकादशी",
    sub_en: "Vishnu vrat & paaran time",
    sub_hi: "विष्णु व्रत और पारण समय",
    href_en: "/ekadashi/",
    href_hi: "/hi/ekadashi/",
  },
  {
    key: "purnima",
    label_en: "Purnima",
    label_hi: "पूर्णिमा",
    sub_en: "Full moon & Satyanarayan vrat",
    sub_hi: "पूर्णिमा और सत्यनारायण व्रत",
    href_en: "/purnima/",
    href_hi: "/hi/purnima/",
  },
  {
    key: "amavasya",
    label_en: "Amavasya",
    label_hi: "अमावस्या",
    sub_en: "New moon & Pitru Tarpan",
    sub_hi: "अमावस्या और पितृ तर्पण",
    href_en: "/amavasya/",
    href_hi: "/hi/amavasya/",
  },
  {
    key: "pradosh",
    label_en: "Pradosh Vrat",
    label_hi: "प्रदोष व्रत",
    sub_en: "Trayodashi Shiva vrat",
    sub_hi: "त्रयोदशी शिव व्रत",
    href_en: "/pradosh/",
    href_hi: "/hi/pradosh/",
  },
  {
    key: "sankashti",
    label_en: "Sankashti Chaturthi",
    label_hi: "संकष्टी चतुर्थी",
    sub_en: "Ganesha obstacle-removal vrat",
    sub_hi: "गणेश बाधा-नाशक व्रत",
    href_en: "/sankashti-chaturthi/",
    href_hi: "/hi/sankashti-chaturthi/",
  },
  {
    key: "masik_shivratri",
    label_en: "Masik Shivratri",
    label_hi: "मासिक शिवरात्रि",
    sub_en: "Monthly Shiva vrat & night vigil",
    sub_hi: "मासिक शिव व्रत और रात्रि जागरण",
    href_en: "/masik-shivratri/",
    href_hi: "/hi/masik-shivratri/",
  },
  {
    key: "sankranti",
    label_en: "Sankranti",
    label_hi: "संक्रांति",
    sub_en: "Solar ingress, snan & daan",
    sub_hi: "सूर्य संक्रमण, स्नान और दान",
    href_en: "/sankranti/",
    href_hi: "/hi/sankranti/",
  },
];

function nextOf(items: VratHubDate[], today: string): VratHubDate | null {
  const upcoming = items
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  return upcoming[0] ?? null;
}

export default function VratCalendarHub({
  years,
  locale,
}: {
  years: readonly VratHubYear[];
  locale: "en" | "hi";
}) {
  const today = currentIndiaDate();

  const rows = useMemo(() => {
    return VRAT_ROWS.map((def) => ({
      def,
      next: nextOf(
        years.flatMap((year) => year.vratas[def.key]),
        today
      ),
    }));
  }, [years, today]);

  return (
    <div className="overflow-x-auto rounded-xl border border-border-default">
      <table className="w-full border-collapse text-left text-body-small">
        <thead>
          <tr className="bg-surface-brand-subtle">
            <th className="px-3 py-2.5 font-semibold text-text-primary">
              {locale === "hi" ? "व्रत" : "Vrat"}
            </th>
            <th className="px-3 py-2.5 font-semibold text-text-primary">
              {locale === "hi" ? "अगली तिथि" : "Next date"}
            </th>
            <th className="px-3 py-2.5 font-semibold text-text-primary">
              {locale === "hi" ? "हिंदू मास" : "Hindu month"}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ def, next }) => {
            const href = locale === "hi" ? def.href_hi : def.href_en;
            const label = locale === "hi" ? def.label_hi : def.label_en;
            const sub = locale === "hi" ? def.sub_hi : def.sub_en;
            return (
              <tr key={def.key} className="border-t border-border-subtle">
                <td className="px-3 py-2.5">
                  <Link href={href} className="font-semibold text-intent-brand underline">
                    {label}
                  </Link>
                  <span className="mt-0.5 block text-caption text-text-muted">{sub}</span>
                </td>
                {/* Machine-readable "next" claim, read by the live correctness
                    check (execution/check_live_page_correctness.py). */}
                <td className="px-3 py-2.5 text-text-primary" data-ishvaram-next-date={next?.date}>
                  {next ? formatVedicEngineDate(next.date, locale) : "—"}
                  {next?.fasting_date && next.fasting_date !== next.date ? (
                    <span className="block text-caption text-text-muted">
                      {locale === "hi" ? "व्रत: " : "Fast on "}
                      {formatVedicEngineDate(next.fasting_date, locale)}
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2.5 text-text-muted">{next?.month_hi || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
